'use client';

import { Dialog, Transition } from '@headlessui/react';
import { cn } from '@lib/util/cn';
import { Fragment } from 'react';
import ModalFooter from './modal-footer';
import ModalHeader from './modal-header';
import TabContent from './tab-content';
import { type Tab, useModalState } from './use-modal-state';
import { useScrollIndicator } from './use-scroll-indicator';

export { useCookieSettingsModal } from './use-cookie-settings-modal';

const TABS: { id: Tab; label: string }[] = [
  { id: 'consent', label: 'Consent' },
  { id: 'details', label: 'Details' },
  { id: 'about', label: 'About' },
];

const noop = () => {};

type CookieSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isDismissible?: boolean;
};

const CookieSettingsModal = ({
  isOpen,
  onClose,
  isDismissible = true,
}: CookieSettingsModalProps) => {
  const {
    activeTab,
    setActiveTab,
    preferences,
    updateCategory,
    handleAcceptAll,
    handleNecessaryOnly,
    handleSavePreferences,
  } = useModalState(isOpen, onClose);

  const { scrollRef, checkScrollPosition, showGradient } =
    useScrollIndicator(activeTab);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[75]"
        onClose={isDismissible ? onClose : noop}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center desktop:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="flex w-full max-w-[640px] max-h-[90vh] flex-col rounded-lg border border-brand-neutral-200 bg-moss-25 shadow-[0px_-4px_6px_rgba(0,0,0,0.08)] overflow-hidden">
                <ModalHeader isDismissible={isDismissible} onClose={onClose} />

                <div className="relative flex flex-1 flex-col min-h-0">
                  <div
                    ref={scrollRef}
                    onScroll={checkScrollPosition}
                    className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 py-8"
                  >
                    <div className="flex items-stretch">
                      {TABS.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveTab(tab.id)}
                          className={cn(
                            'flex flex-1 cursor-pointer items-center justify-center px-[10px] py-[8px] font-body font-bold text-base leading-[1.5] text-brand-neutral-900',
                            activeTab === tab.id
                              ? 'border-b-4 border-brand-interactive-600'
                              : 'border-b-4 border-transparent',
                          )}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    <TabContent
                      activeTab={activeTab}
                      preferences={preferences}
                      onUpdate={updateCategory}
                    />
                  </div>

                  <div
                    aria-hidden="true"
                    className={cn(
                      'pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-moss-25 to-transparent transition-opacity duration-300',
                      showGradient ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </div>

                <ModalFooter
                  activeTab={activeTab}
                  hasSelection={Object.values(preferences).some(Boolean)}
                  onSavePreferences={handleSavePreferences}
                  onNecessaryOnly={handleNecessaryOnly}
                  onAcceptAll={handleAcceptAll}
                  onCustomise={() => setActiveTab('details')}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CookieSettingsModal;
